import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import t from '../theme';

function scoreColor(v) {
  if (!v) return t.text3;
  if (v >= 80) return t.green;
  if (v >= 50) return t.orange;
  return t.red;
}

function Bar({ value, color }) {
  return (
    <div style={{ flex: 1, height: '6px', background: t.surface3, borderRadius: '3px', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${value}%`, background: color, borderRadius: '3px', transition: 'width 0.3s' }}/>
    </div>
  );
}

// ── Student Detail Modal ───────────────────────────────────────────────────
function StudentDetailModal({ student, onClose, onSave }) {
  const [tab,     setTab]     = useState('lessons');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [scores,  setScores]  = useState({});   // { lessonId: { score, note } }
  const [notes,   setNotes]   = useState('');
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`/api/progress/${student.id}`);
      setData(r.data);
      setNotes(r.data.progress?.notes || '');
      const sm = {};
      Object.entries(r.data.scoreMap || {}).forEach(([lid, val]) => {
        sm[lid] = { score: val.score || 0, note: val.note || '' };
      });
      setScores(sm);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [student.id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const scoresArr = Object.entries(scores).map(([lid, val]) => ({
        lesson_item_id: parseInt(lid),
        score: val.score,
        lesson_note: val.note || null,
      }));
      await Promise.all([
        axios.put(`/api/progress/${student.id}`, { notes }),
        axios.put(`/api/progress/${student.id}/scores`, { scores: scoresArr }),
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSave();
    } catch(e) { console.error(e); }
    finally { setSaving(false); }
  };

  const setScore  = (lid, score) => setScores(s => ({ ...s, [lid]: { ...s[lid], score } }));
  const setNote   = (lid, note)  => setScores(s => ({ ...s, [lid]: { ...s[lid], note } }));

  const fmtDate = d => d ? new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—';

  const tabStyle = active => ({
    padding: '8px 16px', borderRadius: '8px', fontSize: '13px',
    fontWeight: active ? 600 : 400, cursor: 'pointer', border: 'none',
    fontFamily: t.font, background: active ? t.accentLight : 'none',
    color: active ? t.accent : t.text3,
    borderBottom: active ? `2px solid ${t.accent}` : '2px solid transparent',
  });

  return (
    <div style={t.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...t.modal, maxWidth: '760px', maxHeight: '92vh' }}>

        {/* Header */}
        <div style={{ ...t.modalHeader, background: '#111110' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: t.accentLight,
              border: `1px solid ${t.accentBorder}`, display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: '16px', fontWeight: 700, color: t.accent }}>
              {student.full_name[0]}
            </div>
            <div>
              <h3 style={{ ...t.modalTitle, color: '#f0ede8' }}>{student.full_name}</h3>
              <p style={{ fontSize: '12px', color: t.text3, margin: 0 }}>@{student.user?.username}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button style={{ ...t.btnPrimary, padding: '8px 16px', fontSize: '13px' }}
              onClick={handleSave} disabled={saving}>
              {saved ? '✓ Saved!' : saving ? 'Saving...' : 'Save Progress'}
            </button>
            <button style={t.closeBtn} onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', padding: '0 20px',
          borderBottom: `1px solid ${t.border}`, background: t.surface }}>
          {[
            { key: 'lessons',  label: 'Lessons' },
            { key: 'classes',  label: 'Classes' },
            { key: 'homework', label: 'Homework' },
          ].map(({ key, label }) => (
            <button key={key} style={tabStyle(tab === key)} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>

        <div style={{ ...t.modalBody, padding: '20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: t.text3, padding: '48px' }}>Loading...</div>
          ) : (
            <>
              {/* ── LESSONS TAB ── */}
              {tab === 'lessons' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {!data?.classes?.length ? (
                    <p style={{ color: t.text3, fontStyle: 'italic', fontSize: '13px' }}>No lessons assigned yet.</p>
                  ) : (
                    <>
                      {/* Group lessons by taal from assigned classes */}
                      {(() => {
                        const allLessons = (data?.classes || []).flatMap(cls =>
                          (cls.lessonItems || []).map(l => ({ ...l, className: cls.title }))
                        );
                        const byTaal = {};
                        allLessons.forEach(l => {
                          const tId = l.taal_id;
                          if (!byTaal[tId]) byTaal[tId] = { taal: l.taal, byType: {} };
                          const tyId = l.type_id;
                          if (!byTaal[tId].byType[tyId]) byTaal[tId].byType[tyId] = { lessonType: l.lessonType, lessons: [] };
                          byTaal[tId].byType[tyId].lessons.push(l);
                        });
                        return Object.values(byTaal).map(({ taal, byType }) => (
                          <div key={taal?.id} style={{ background: t.surface2, borderRadius: t.r,
                            border: `1px solid ${t.border}`, overflow: 'hidden' }}>
                            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${t.border}`,
                              display: 'flex', alignItems: 'center', gap: '8px',
                              background: t.surface3 }}>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: t.accent }}>{taal?.name}</span>
                            </div>
                            {Object.values(byType).map(({ lessonType, lessons }) => (
                              <div key={lessonType?.id} style={{ padding: '10px 14px', borderBottom: `1px solid ${t.border}` }}>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: t.text2,
                                  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                                  {lessonType?.name}
                                </div>
                                {lessons.map(lesson => {
                                  const val = scores[lesson.id]?.score ?? 0;
                                  const note = scores[lesson.id]?.note ?? '';
                                  const c = scoreColor(val);
                                  return (
                                    <div key={lesson.id} style={{ marginBottom: '14px', paddingBottom: '14px',
                                      borderBottom: `1px solid ${t.border}` }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                        <span style={{ fontSize: '13px', color: t.text, flex: 1, fontWeight: 500 }}>{lesson.title}</span>
                                        <span style={{ fontSize: '11px', color: t.text3 }}>{lesson.className}</span>
                                        <div style={{ ...{ background: c+'18', color: c, padding: '2px 10px',
                                          borderRadius: '20px', fontSize: '12px', fontWeight: 700 } }}>{val}%</div>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                        <input type="range" min={0} max={100} value={val}
                                          onChange={e => setScore(lesson.id, Number(e.target.value))}
                                          style={{ flex: 1, cursor: 'pointer', accentColor: c }}/>
                                      </div>
                                      <input
                                        style={{ ...t.input, fontSize: '12px', padding: '6px 10px' }}
                                        placeholder="Note for this lesson (optional)..."
                                        value={note}
                                        onChange={e => setNote(lesson.id, e.target.value)}/>
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        ));
                      })()}

                      {/* Overall notes */}
                      <div style={{ background: t.surface2, borderRadius: t.r, padding: '14px',
                        border: `1px solid ${t.border}` }}>
                        <label style={{ ...t.label, marginBottom: '8px', display: 'block' }}>Overall Teacher Notes</label>
                        <textarea style={{ ...t.textarea, resize: 'vertical' }} rows={3}
                          placeholder="General observations, strengths, areas to improve..."
                          value={notes} onChange={e => setNotes(e.target.value)}/>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── CLASSES TAB ── */}
              {tab === 'classes' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {!data?.classes?.length ? (
                    <p style={{ color: t.text3, fontStyle: 'italic', fontSize: '13px' }}>Not enrolled in any classes.</p>
                  ) : data.classes.map(cls => (
                    <div key={cls.id} style={{ background: t.surface2, borderRadius: t.r,
                      border: `1px solid ${t.border}`, overflow: 'hidden' }}>
                      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${t.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: t.surface3 }}>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: t.text }}>{cls.title}</span>
                        <span style={{ fontSize: '12px', color: t.text3 }}>{cls.lessonItems?.length || 0} lessons</span>
                      </div>
                      {cls.lessonItems?.length ? (
                        <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {cls.lessonItems.map(l => {
                            const val = scores[l.id]?.score ?? 0;
                            const c = scoreColor(val);
                            return (
                              <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '13px', color: t.text, marginBottom: '2px' }}>{l.title}</div>
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    {l.taal && <span style={{ background: t.accentLight, color: t.accent, padding: '1px 6px', borderRadius: '20px', fontSize: '10px' }}>{l.taal.name}</span>}
                                    {l.lessonType && <span style={{ background: t.surface3, color: t.text3, padding: '1px 6px', borderRadius: '20px', fontSize: '10px' }}>{l.lessonType.name}</span>}
                                  </div>
                                </div>
                                <Bar value={val} color={c}/>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: c, minWidth: '36px', textAlign: 'right' }}>{val}%</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p style={{ padding: '12px 16px', fontSize: '12px', color: t.text3, fontStyle: 'italic', margin: 0 }}>No lessons in this class.</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* ── HOMEWORK TAB ── */}
              {tab === 'homework' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {!data?.homework?.length ? (
                    <p style={{ color: t.text3, fontStyle: 'italic', fontSize: '13px' }}>No homework assigned.</p>
                  ) : data.homework.map(hs => {
                    const hw = hs.homework_item || hs.homework;
                    if (!hw) return null;
                    const overdue = hw.due_date && new Date(hw.due_date) < new Date() && hs.status !== 'completed';
                    const STATUS = {
                      assigned:    { color: t.blue,   bg: t.blueLight,   label: 'Assigned' },
                      in_progress: { color: t.orange, bg: t.orangeLight, label: 'In Progress' },
                      completed:   { color: t.green,  bg: t.greenLight,  label: 'Completed' },
                    };
                    const st = STATUS[hs.status] || STATUS.assigned;
                    return (
                      <div key={hs.id} style={{ background: t.surface2, borderRadius: t.r,
                        padding: '14px', border: `1px solid ${t.border}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '14px', fontWeight: 500, color: t.text, flex: 1 }}>{hw.title}</span>
                          <span style={{ background: st.bg, color: st.color, padding: '2px 10px',
                            borderRadius: '20px', fontSize: '11px', fontWeight: 600 }}>{st.label}</span>
                          {overdue && <span style={{ background: t.redLight, color: t.red, padding: '2px 8px',
                            borderRadius: '20px', fontSize: '11px', fontWeight: 600 }}>Overdue</span>}
                        </div>
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                          {hw.taal && <span style={{ background: t.accentLight, color: t.accent, padding: '1px 8px', borderRadius: '20px', fontSize: '11px' }}>{hw.taal.name}</span>}
                          {hw.lessonType && <span style={{ background: t.surface3, color: t.text3, padding: '1px 8px', borderRadius: '20px', fontSize: '11px' }}>{hw.lessonType.name}</span>}
                          {hw.due_date && <span style={{ fontSize: '11px', color: overdue ? t.red : t.text3 }}>Due: {fmtDate(hw.due_date)}</span>}
                        </div>
                        {hs.submission_audio_s3_key && (
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '11px', color: t.text3, marginBottom: '4px' }}>Submission:</div>
                            {/\.(mp3|wav|m4a|aac|ogg)$/i.test(hs.submission_audio_s3_key)
                              ? <audio controls style={{ width: '100%' }}><source src={hs.submission_audio_s3_key}/></audio>
                              : <video controls style={{ width: '100%', maxHeight: '120px', borderRadius: '6px' }}><source src={hs.submission_audio_s3_key}/></video>}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', color: t.text3 }}>Score:</span>
                            <input type="number" min="0" max="10"
                              style={{ ...t.input, width: '60px', padding: '4px 8px', fontSize: '12px' }}
                              defaultValue={hs.score || ''}
                              placeholder="—"
                              onBlur={async e => {
                                const score = e.target.value ? parseInt(e.target.value) : null;
                                try { await axios.put(`/api/homework/${hw.id}/students/${student.id}`, { score }); }
                                catch(err) { console.error(err); }
                              }}/>
                            <span style={{ fontSize: '11px', color: t.text3 }}>/10</span>
                          </div>
                          <select
                            style={{ ...t.select, padding: '4px 10px', fontSize: '12px', width: 'auto' }}
                            defaultValue={hs.status || 'assigned'}
                            onChange={async e => {
                              try { await axios.put(`/api/homework/${hw.id}/students/${student.id}`, { status: e.target.value }); }
                              catch(err) { console.error(err); }
                            }}>
                            <option value="assigned">Assigned</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </select>
                        </div>
                        {hs.submission_notes && (
                          <p style={{ fontSize: '12px', color: t.text2, margin: '8px 0 0', fontStyle: 'italic' }}>{hs.submission_notes}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Admin Progress Page ────────────────────────────────────────────────────
function AdminProgressPage() {
  const [students,     setStudents]     = useState([]);
  const [lessonGroups, setLessonGroups] = useState({});
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [selected,     setSelected]     = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get('/api/progress');
      setStudents(r.data.students || []);
      setLessonGroups(r.data.lessonGroups || {});
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const studentAvg = s => {
    const vals = Object.values(s.scoreMap || {}).map(v => v.score || 0);
    return vals.length ? Math.round(vals.reduce((a,v)=>a+v,0)/vals.length) : 0;
  };

  const filtered = students.filter(s =>
    !search || s.full_name.toLowerCase().includes(search.toLowerCase())
  );

  const totalLessons = Object.values(lessonGroups)
    .flatMap(g => Object.values(g.types))
    .reduce((s, t) => s + t.lessons.length, 0);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
        marginBottom:'20px', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h2 style={{ fontSize:'22px', fontWeight:600, color:t.text, margin:0, letterSpacing:'-0.3px' }}>Student Progress</h2>
          <p style={{ fontSize:'13px', color:t.text3, margin:'4px 0 0' }}>{students.length} students · {totalLessons} lessons tracked</p>
        </div>
        <input style={{ ...t.input, width:'220px' }} placeholder="🔍 Search students..."
          value={search} onChange={e => setSearch(e.target.value)}/>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', color:t.text3, padding:'48px' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...t.card, padding:'48px', textAlign:'center' }}>
          <p style={{ color:t.text3 }}>{search ? 'No students match' : 'No students yet'}</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
          {filtered.map(st => {
            const avg = studentAvg(st);
            const scored = Object.values(st.scoreMap || {}).filter(v => v.score > 0).length;
            const c = scoreColor(avg);
            return (
              <div key={st.id} style={{ ...t.card, padding:'16px 20px',
                display:'flex', alignItems:'center', gap:'16px', flexWrap:'wrap' }}>
                <div style={{ width:'40px', height:'40px', borderRadius:'50%', background:t.accent,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'16px', fontWeight:700, color:'#161614', flexShrink:0 }}>
                  {st.full_name[0]}
                </div>
                <div style={{ flex:1, minWidth:'160px' }}>
                  <div style={{ fontSize:'15px', fontWeight:600, color:t.text, marginBottom:'4px' }}>{st.full_name}</div>
                  <div style={{ fontSize:'12px', color:t.text3 }}>@{st.user?.username} · {scored} lessons scored</div>
                </div>
                <div style={{ flex:1, display:'flex', flexDirection:'column', gap:'6px', minWidth:'200px' }}>
                  {Object.values(lessonGroups).slice(0,3).map(({ taal, types }) => {
                    const ids = Object.values(types).flatMap(tp => tp.lessons.map(l => l.id));
                    const vals = ids.map(id => st.scoreMap?.[id]?.score || 0);
                    const avg = vals.length ? Math.round(vals.reduce((a,v)=>a+v,0)/vals.length) : 0;
                    const c2 = scoreColor(avg);
                    return (
                      <div key={taal.id} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                        <span style={{ fontSize:'11px', color:t.text3, width:'80px', flexShrink:0 }}>{taal.name}</span>
                        <Bar value={avg} color={c2}/>
                        <span style={{ fontSize:'11px', fontWeight:700, color:c2, minWidth:'28px', textAlign:'right' }}>{avg}%</span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'12px', flexShrink:0 }}>
                  <div style={{ width:'52px', height:'52px', borderRadius:'50%',
                    background: c+'18', border:`2px solid ${c}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:'13px', fontWeight:800, color:c }}>
                    {avg}%
                  </div>
                  <button style={{ ...t.btnPrimary, padding:'8px 16px', fontSize:'13px' }}
                    onClick={() => setSelected(st)}>
                    View Progress
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <StudentDetailModal
          student={selected}
          onClose={() => setSelected(null)}
          onSave={load}
        />
      )}
    </div>
  );
}

// ── Student Progress Page ──────────────────────────────────────────────────
function StudentProgressPage() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/progress')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign:'center', color:t.text3, padding:'48px' }}>Loading...</div>;

  const { progress, scoreMap, lessonGroups } = data || {};

  return (
    <div>
      <div style={{ marginBottom:'24px' }}>
        <h2 style={{ fontSize:'22px', fontWeight:600, color:t.text, margin:0 }}>My Progress</h2>
        <p style={{ fontSize:'13px', color:t.text3, margin:'4px 0 0' }}>Your lesson scores and teacher notes</p>
      </div>

      {!Object.keys(lessonGroups || {}).length ? (
        <div style={{ ...t.card, padding:'48px', textAlign:'center' }}>
          <p style={{ color:t.text3 }}>No lessons assigned yet.</p>
        </div>
      ) : (
        <>
          {Object.values(lessonGroups).map(({ taal, types }) => (
            <div key={taal.id} style={{ ...t.card, padding:'16px 18px', marginBottom:'12px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', paddingBottom:'10px',
                borderBottom:`1px solid ${t.border}`, marginBottom:'12px' }}>
                <span style={{ fontSize:'15px', fontWeight:700, color:t.accent }}>{taal.name}</span>
                {(() => {
                  const ids = Object.values(types).flatMap(tp => tp.lessons.map(l => l.id));
                  const vals = ids.map(id => scoreMap?.[id]?.score || 0);
                  const avg = vals.length ? Math.round(vals.reduce((a,v)=>a+v,0)/vals.length) : 0;
                  const c = scoreColor(avg);
                  return <span style={{ marginLeft:'auto', fontSize:'13px', fontWeight:700, color:c }}>{avg}% avg</span>;
                })()}
              </div>
              {Object.values(types).map(({ lessonType, lessons }) => (
                <div key={lessonType.id} style={{ paddingLeft:'8px', marginBottom:'12px' }}>
                  <div style={{ fontSize:'11px', fontWeight:600, color:t.text3, textTransform:'uppercase',
                    letterSpacing:'0.5px', marginBottom:'8px' }}>{lessonType.name}</div>
                  {lessons.map(lesson => {
                    const val = scoreMap?.[lesson.id]?.score || 0;
                    const note = scoreMap?.[lesson.id]?.note;
                    const c = scoreColor(val);
                    return (
                      <div key={lesson.id} style={{ display:'flex', alignItems:'center', gap:'10px',
                        paddingLeft:'16px', marginBottom:'10px' }}>
                        <span style={{ fontSize:'13px', color:t.text, flex:1 }}>{lesson.title}</span>
                        <Bar value={val} color={c}/>
                        <span style={{ fontSize:'13px', fontWeight:700, color:c, minWidth:'40px', textAlign:'right' }}>{val}%</span>
                        {note && <span style={{ fontSize:'11px', color:t.text3, fontStyle:'italic', maxWidth:'120px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={note}>📝 {note}</span>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}

          {progress?.notes && (
            <div style={{ ...t.card, padding:'16px 18px' }}>
              <div style={{ fontSize:'12px', fontWeight:600, color:t.accent, textTransform:'uppercase',
                letterSpacing:'0.5px', marginBottom:'8px' }}>Teacher Notes</div>
              <p style={{ fontSize:'13px', color:t.text2, margin:0, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{progress.notes}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────
export default function ProgressPage() {
  const { isAdmin } = useAuth();
  return isAdmin ? <AdminProgressPage /> : <StudentProgressPage />;
}
